/**
 * The console shell: the sign-in gate, the menu of §11.7, and the chrome
 * around every view.
 *
 * The menu is **not** a list in this file. It comes back from
 * `public.console_menu`, which reads `public.console_views` — rev7 §11.7's own
 * table of views and roles — against the person's current roles. So "a login
 * per role sees the right menu" is a fact about the data, and a view another
 * stage of `build/QUEUE.md` builds is still listed, with the stage that owns
 * it named, rather than silently missing from a role that rev7 gives it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { readPublicConfig } from '../lib/env.ts'
import { SiteNotice } from '../routes/SiteNotice.tsx'
import { consoleCall, ConsoleError } from './api.ts'
import { ConsoleContext } from './context.ts'
import {
  clearSession,
  readSession,
  takeReturnPath,
  writeSession,
  type ConsoleStaff,
  type ConsoleView,
  type StoredSession,
} from './session.ts'
import { SignIn } from './SignIn.tsx'
import { CONSOLE_STYLES } from './styles.ts'

interface SignInAnswer {
  provider: 'google' | 'seeded'
  staff: ConsoleStaff
  views: ConsoleView[]
  app_env?: string
}

/** Supabase Auth comes back with the session in the URL fragment. Reading it
 *  here rather than through a client library keeps the console's dependencies
 *  where they are: one redirect out, one token back. */
function accessTokenFromHash(hash: string): string | null {
  if (!hash.startsWith('#')) return null
  const token = new URLSearchParams(hash.slice(1)).get('access_token')
  return token === null || token.trim() === '' ? null : token
}

export function ConsoleShell(): JSX.Element {
  const config = useMemo(() => readPublicConfig(), [])
  const [session, setSessionState] = useState<StoredSession | null>(() => readSession())
  const [signingIn, setSigningIn] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const setSession = useCallback((next: StoredSession | null) => {
    if (next === null) clearSession()
    else writeSession(next)
    setSessionState(next)
  }, [])

  // The Google round trip. The access token arrives in the fragment; this
  // exchanges it for the menu, binds the Google account identifier to the
  // access-sheet row (§11.12.2) and takes the fragment back off the URL so a
  // reload does not replay it.
  useEffect(() => {
    const token = accessTokenFromHash(globalThis.location?.hash ?? '')
    if (token === null || session !== null) return
    let live = true
    setSigningIn(true)
    consoleCall<SignInAnswer>('sign_in_google', {}, { config, session: null, accessToken: token })
      .then((answer) => {
        if (!live) return
        setSession({
          provider: 'google',
          staff: answer.staff,
          views: answer.views,
          appEnv: answer.app_env ?? config.appEnv,
          accessToken: token,
        })
        // Run 12 item G: back to the page the staff member was sent to.
        navigate(takeReturnPath(), { replace: true })
      })
      .catch((cause: unknown) => {
        if (!live) return
        setError(cause instanceof ConsoleError ? cause.message : 'A bejelentkezés nem sikerült.')
      })
      .finally(() => live && setSigningIn(false))
    return () => {
      live = false
    }
  }, [config, session, setSession, navigate])

  const value = useMemo(() => ({ config, session, setSession }), [config, session, setSession])

  if (session === null) {
    return (
      <ConsoleContext.Provider value={value}>
        <SiteNotice />
        {signingIn
          ? (
            <div className="plt-console">
              <style>{CONSOLE_STYLES}</style>
              <main><p>Bejelentkezés…</p></main>
            </div>
          )
          : (
            <>
              {error !== null && (
                <div className="plt-console">
                  <style>{CONSOLE_STYLES}</style>
                  <main><p className="note error">{error}</p></main>
                </div>
              )}
              <SignIn appEnv={config.appEnv} />
            </>
          )}
      </ConsoleContext.Provider>
    )
  }

  return (
    <ConsoleContext.Provider value={value}>
      <div className="plt-console" data-testid="console">
        <style>{CONSOLE_STYLES}</style>
        <SiteNotice />

        <header className="bar">
          <h1>Planteen · munkatársi konzol</h1>
          {session.appEnv !== null && session.appEnv !== '' && (
            <span className="env" data-testid="console-env">{session.appEnv}</span>
          )}
          <span className="who" data-testid="console-who">
            {session.staff.name} · {session.staff.roles.join(' · ')}
          </span>
          <button type="button" onClick={() => setSession(null)} data-testid="sign-out">
            Kilépés
          </button>
        </header>

        <nav className="menu" aria-label="Nézetek" data-testid="console-menu">
          {session.views.map((view) => (
            <NavLink
              key={view.key}
              to={view.path}
              end
              className={view.built ? undefined : 'pending'}
              title={view.note ?? undefined}
              data-testid={`menu-${view.key}`}
            >
              {view.label}
            </NavLink>
          ))}
        </nav>

        <main key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </ConsoleContext.Provider>
  )
}
